import { useState, useEffect, useCallback } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

export const useURLParamState = (paramName, defaultValue = null) => {
  const history = useHistory();
  const location = useLocation();

  const getParamValue = useCallback(() => {
    const searchParams = new URLSearchParams(location.search);
    const value = searchParams.get(paramName);

    if (value === null) return defaultValue;

    // Try to parse as JSON for complex values, fallback to string
    try {
      return JSON.parse(value);
    } catch {
      return value;
    }
  }, [location.search, paramName, defaultValue]);

  const [state, setState] = useState(getParamValue);

  // Update state when URL changes
  useEffect(() => {
    setState(getParamValue());
  }, [getParamValue]);

  const setURLParamState = useCallback(
    newValue => {
      const searchParams = new URLSearchParams(location.search);

      if (
        newValue === null ||
        newValue === undefined ||
        newValue === defaultValue
      ) {
        searchParams.delete(paramName);
      } else {
        const valueToSet =
          typeof newValue === 'string' ? newValue : JSON.stringify(newValue);
        searchParams.set(paramName, valueToSet);
      }

      const newSearch = searchParams.toString();
      const newPath = newSearch
        ? `${location.pathname}?${newSearch}`
        : location.pathname;

      history.replace(newPath);
    },
    [history, location.pathname, location.search, paramName, defaultValue]
  );

  return [state, setURLParamState];
};
